import type { ReactNode } from 'react';

/**
 * The product's icons, as inline SVG: a handful of glyphs is not worth an icon library's
 * bytes on a phone. Every one is `aria-hidden` — the control beside it carries the words,
 * and the icon adds recognition, not information.
 *
 * Provider tiles use generic glyphs (an envelope, a sheet), never Google's or Microsoft's
 * marks, which have their own usage rules.
 */
function Svg({ children, size = 22 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export const HomeIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M3.5 10.5 12 4l8.5 6.5" /><path d="M5.5 9.5V19a1 1 0 0 0 1 1h3.5v-5.5h4V20h3.5a1 1 0 0 0 1-1V9.5" /></Svg>
);
export const CameraGlyph = ({ size }: { size?: number }) => (
  <Svg size={size}>
    <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.3 4.7h5.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h2.2A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
    <circle cx="12" cy="13" r="3.4" />
  </Svg>
);
export const ReviewIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c.6-3 2.9-4.8 5.5-4.8s4.9 1.8 5.5 4.8" /><path d="m15 11 2 2 4-4" /></Svg>
);
export const SettingsIcon = ({ size }: { size?: number }) => (
  <Svg size={size}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
  </Svg>
);
export const MailIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 6.5 8.5 6.5 8.5-6.5" /></Svg>
);
export const TrayIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M4 13.5 6.2 6a1.5 1.5 0 0 1 1.4-1h8.8a1.5 1.5 0 0 1 1.4 1L20 13.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18z" /><path d="M4 13.5h4.5l1 2h5l1-2H20" /></Svg>
);
export const SheetIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8z" /><path d="M14 3.5V8h4.5" /><path d="M8.5 12h7M8.5 15h7M12 12v6" /></Svg>
);
export const CloudUpIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M7 18.5a4.5 4.5 0 0 1-.6-9 6 6 0 0 1 11.5 1.6 3.8 3.8 0 0 1-.4 7.4" /><path d="M12 12v8M9 15l3-3 3 3" /></Svg>
);
export const PlaneIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M21 3 3.5 10.2c-.8.3-.7 1.4.1 1.6l6.2 1.6 1.6 6.2c.2.8 1.3.9 1.6.1z" /><path d="m9.8 13.4 4.6-4.6" /></Svg>
);
export const BellIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 0 0 4 0" /></Svg>
);
export const CheckCircleIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12.2 2.4 2.4 4.6-4.8" /></Svg>
);
export const AlertIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M12 4 2.8 19.5h18.4z" /><path d="M12 10v4.5M12 17.2v.1" /></Svg>
);
export const KeyIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="8" cy="15" r="4" /><path d="m11 12 8.5-8.5M16.5 6.5l2 2M14.5 8.5l1.5 1.5" /></Svg>
);
export const ChevronRight = ({ size = 18 }: { size?: number }) => (
  <Svg size={size}><path d="m9 6 6 6-6 6" /></Svg>
);
